import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquareQuote, Clock, ShieldAlert, HelpCircle, MessagesSquare, Tag, Gift, Award } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { useAgo, usePriceText } from "@/components/dm/ui";
import { EmptyState } from "@/components/dm/EmptyState";

const ICON = { new_request: MessageSquareQuote, contact_check: HelpCircle, expiring: Clock, post_hidden: ShieldAlert, post_restored: ShieldAlert, post_removed: ShieldAlert, warning: ShieldAlert, new_question: MessagesSquare, question_answered: MessagesSquare, saved_answered: MessagesSquare, saved_price_drop: Tag, referral_reward: Gift, spotlight_approved: Award, spotlight_rejected: Award };

export default function Notifications() {
  const { t, user, ready, L, hubOf, setUnread, setAuth } = useApp();
  const [items, setItems] = useState(null);
  const ago = useAgo();
  const price = usePriceText();
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); });
  useEffect(() => { if (user) load(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ready) return null;
  if (!user) return <div className="max-w-xl mx-auto p-6"><EmptyState title={t("signup_prompt")}><button data-testid="notif-signup" onClick={() => setAuth({ mode: "prompt" })} className="press min-h-[52px] px-8 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold">{t("create_account")}</button></EmptyState></div>;
  const money = (v) => (v ? price({ price: v, type: "listing" }) : "");
  const text = (n) => t(`n_${n.type}`, { city: L(hubOf(n.params?.hub_id)), title: n.params?.title || "", name: n.params?.name || "", old: money(n.params?.old), new: money(n.params?.new) });
  const open = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); setUnread((u) => Math.max(0, u - 1)); }
    if (n.type === "referral_reward") { navigate("/invite"); return; }
    navigate(n.type === "contact_check" || n.type === "expiring" ? "/my-posts" : n.post_id && n.type !== "post_removed" ? `/post/${n.post_id}${/question|answered/.test(n.type) ? "#questions" : ""}` : "/my-posts");
  };
  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5" data-testid="notifications-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t("notifications")}</h1>
        {items?.some((n) => !n.read) && <button data-testid="mark-all-read" onClick={() => api.post("/notifications/read-all").then(load)} className="press min-h-[44px] px-4 rounded-full bg-[#F3EDE4] text-sm font-bold">{t("mark_all_read")}</button>}
      </div>
      {items === null ? <div className="skeleton h-20 rounded-2xl" /> : items.length === 0 ? <EmptyState title={t("no_notifications")} /> : (
        <div className="space-y-2">
          {items.map((n) => {
            const I = ICON[n.type] || Bell;
            return (
              <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => open(n)} className={`press w-full text-left flex gap-3 p-4 rounded-2xl border ${n.read ? "bg-[#FFFDF9] border-[#E8E2D9]" : "bg-[#FEF3C7]/60 border-[#F59E0B]/50"}`}>
                <span className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${n.type === "new_request" ? "bg-[#C2410C] text-white" : "bg-[#1E4D3B] text-[#F59E0B]"}`}><I className="w-5 h-5" /></span>
                <span className="flex-1 min-w-0"><span className="block font-semibold leading-snug">{text(n)}</span><span className="text-xs text-[#6B5E55]">{ago(n.created_at)}</span></span>
                {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-[#C2410C] mt-2" aria-label="unread" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
